import type { User, UserStatus } from "sdk";
import { createResource, createUniqueId, Show, type VoidProps } from "solid-js";
import { generatePfp, pfpsLoaded } from "@/lib/pfp";
import { getThumbFromId } from "@/media/util";

const SIZE = 64;
const STATUS_RADIUS = 8;
const STATUS_MARGIN = 8;

const MARGIN = STATUS_RADIUS / 2;
const TOTAL_SIZE = SIZE + MARGIN * 2;

export type AvatarProps2 = {
	/**
	 * the user who's avatar to show
	 *
	 * if the user does not have an avatar, one will be generated
	 */
	user: User;
	// room_member?: RoomMember,

	/**
	 * whether to include padding round the avatar
	 *
	 * if false, the status indicator may clip outside of the image. defaults to true.
	 */
	pad?: boolean;

	/** whether to animate the avatar (for animated gif/webp avatars)*/
	animate?: boolean;

	/** whether to show the user's status */
	status?: boolean;

	/** additional css styles to include */
	style?: string;
};

export type AvatarProps = {
	user?: User;
	pad?: number;
	animate?: boolean;
	style?: string;
	typing?: boolean;
	extraSpacing?: boolean; // for typing
	// room_member?: RoomMember,
	// thread_member?: ThreadMember,
	// TODO: pad?: boolean for <Avatar> (but not <AvatarWithStatus>) to disable padding
};

export const getStatusPath = (status: UserStatus) => {
	const cx = 64;
	const cy = 64;
	const r = 12;

	switch (status) {
		case "Away": {
			const sweep = 2;
			return `M ${cx} ${cy - r} a ${r} ${r} 0 1 1 ${-r} ${r} a ${sweep} ${sweep} 0 0 0 ${r} ${-r} Z`;
		}

		case "Busy": {
			const s = 3;
			const w = r;
			const h = s * 2;
			return `M ${cx} ${cy - r} a ${r} ${r} 0 1 1 0 ${r * 2} a ${r} ${r} 0 1 1 0 -${r * 2} Z M ${cx - w / 2} ${cy - s} h ${w} a ${s} ${s} 0 0 1 0 ${h} h ${-w} a ${s} ${s} 0 0 1 0 ${-h} Z`;
		}

		case "Offline": {
			const s = 6;
			const i = r - s;
			return `M ${cx} ${cy - r} a ${r} ${r} 0 1 1 0 ${r * 2} a ${r} ${r} 0 1 1 0 -${r * 2} Z M ${cx} ${cy - r + s} a ${i} ${i} 0 1 1 0 ${i * 2} a ${i} ${i} 0 1 1 0 -${i * 2} Z`;
		}

		case "Online":
		default: {
			return `M ${cx} ${cy - r} a ${r} ${r} 0 1 1 0 ${r * 2} a ${r} ${r} 0 1 1 0 -${r * 2} Z`;
		}
	}
};

export const AvatarDefs = () => {
	return (
		<defs>
			<mask id="rbox">
				<rect
					rx="6"
					width={SIZE}
					height={SIZE}
					x={MARGIN}
					y={MARGIN}
					fill="white"
				/>
				<circle
					cx={SIZE}
					cy={SIZE}
					r={STATUS_RADIUS + STATUS_MARGIN}
					fill="black"
				/>
			</mask>
		</defs>
	);
};

export const Avatar2 = (props: VoidProps<AvatarProps2>) => {
	const [pfp] = createResource(
		() => [props.user, pfpsLoaded(), props.animate] as const,
		async ([user, loaded, anim]) => {
			if (user.avatar) return getThumbFromId(user.avatar, 64, anim);
			if (!loaded) return "";
			return generatePfp(user.id);
		},
	);

	const status = () => props.user?.presence.status ?? "Offline";
	const pad = () => ((props.pad ?? true) ? "" : `margin: -${MARGIN}px;`);

	return (
		<svg
			aria-hidden="true"
			role="img"
			class="avatar2"
			data-status={status()}
			data-user-id={props.user.id}
			data-generated={!props.user.avatar}
			viewBox={`0 0 ${TOTAL_SIZE} ${TOTAL_SIZE}`}
			style={pad() + props.style}
			classList={{ unpadded: props.pad === false }}
		>
			<g mask={props.status ? "url(#rbox)" : undefined}>
				<rect
					class="backdrop"
					width={SIZE}
					height={SIZE}
					x={MARGIN}
					y={MARGIN}
				/>
				<image
					// TODO: properly crop avatars on upload
					preserveAspectRatio="xMidYMid slice"
					width={SIZE}
					height={SIZE}
					x={MARGIN}
					y={MARGIN}
					href={pfp()}
				/>
			</g>
			<Show when={props.status}>
				<path class="status" d={getStatusPath(status())} />
			</Show>
		</svg>
	);
};

export const AvatarWithStatus = (props: VoidProps<AvatarProps>) => {
	const size = 64;
	const circPos = size;
	const circRad = 12;
	const circPad = 8;
	const typingPillWidth = 48;

	const pad = () => props.pad ?? 8;
	const totalSize = () => size + pad() * 2;
	const typing = () => props.typing ?? false;

	const [pfp] = createResource(
		() => [props.user?.id, pfpsLoaded()],
		async ([userId, loaded]) => {
			if (!userId || !loaded) return "";
			return generatePfp(userId as string);
		},
	);

	const id = createUniqueId();
	const status = () => props.user?.presence.status ?? "Offline";

	const indHeight = circRad * 2;
	const indRight = () => (typing() ? circPos + circRad * 2 : circPos + circRad);
	const indWidth = () => (typing() ? typingPillWidth : indHeight);
	const indX = () => indRight() - indWidth();
	const dotsX = () => indRight() - typingPillWidth / 2;

	return (
		<svg
			aria-hidden="true"
			class="avatar status-indicator"
			data-status={status()}
			viewBox={`0 0 ${totalSize() + (props.extraSpacing ? 8 : 0)} ${totalSize()}`}
			role="img"
			style={`--pad: ${pad()}px;` + (props.style ?? "")}
			classList={{ autogenerated: !props.user?.avatar }}
		>
			<defs>
				<mask id={`rbox-${id}`}>
					<rect
						rx="6"
						width={size}
						height={size}
						x={pad()}
						y={pad()}
						fill="white"
					/>
					<rect
						class="cutout"
						fill="black"
						y={circPos - circRad - circPad}
						height={indHeight + circPad * 2}
						rx={circRad + circPad}
						style={{
							x: `${indX() - circPad}px`,
							width: `${indWidth() + circPad * 2}px`,
						}}
					/>
					<clipPath id={`pill-${id}`}>
						<rect
							class="typing-pill-clip"
							y={circPos - circRad}
							height={indHeight}
							rx={circRad}
							style={{
								x: `${indX()}px`,
								width: `${indWidth()}px`,
							}}
						/>
					</clipPath>
				</mask>
			</defs>
			<g mask={`url(#rbox-${id})`}>
				<Show
					when={props.user?.avatar}
					fallback={
						<image
							preserveAspectRatio="xMidYMid slice"
							width={size}
							height={size}
							x={pad()}
							y={pad()}
							href={pfp() ?? ""}
						/>
					}
				>
					{(avatar) => (
						<>
							<rect
								width={size}
								height={size}
								x={pad()}
								y={pad()}
								fill="oklch(var(--color-bg3))"
							/>
							<image
								// temp? i need to crop avatars properly on upload
								preserveAspectRatio="xMidYMid slice"
								width={size}
								height={size}
								x={pad()}
								y={pad()}
								href={getThumbFromId(avatar(), 64, props.animate)}
							/>
						</>
					)}
				</Show>
			</g>
			<path
				class="indicator"
				d={getStatusPath(status())}
				style={{ opacity: typing() ? 0 : 1 }}
			/>
			<rect
				class="typing-pill indicator"
				y={circPos - circRad}
				height={indHeight}
				rx={circRad}
				style={{
					x: `${indX()}px`,
					width: `${indWidth()}px`,
					opacity: typing() ? 1 : 0,
				}}
			/>
			<g
				class="typing-dots"
				clip-path={`url(#pill-${id})`}
				style={{ opacity: typing() ? 1 : 0 }}
			>
				<circle
					class="dot"
					cx={dotsX() - 12}
					cy={circPos}
					style="animation-delay: 0s"
				/>
				<circle
					class="dot"
					cx={dotsX()}
					cy={circPos}
					style="animation-delay: 150ms"
				/>
				<circle
					class="dot"
					cx={dotsX() + 12}
					cy={circPos}
					style="animation-delay: 300ms"
				/>
			</g>
		</svg>
	);
};

export const Avatar = (props: VoidProps<AvatarProps>) => {
	const size = 64;
	const pad = () => props.pad ?? 4;
	const totalSize = () => size + pad() * 2;

	const [pfp] = createResource(
		() => [props.user?.id, pfpsLoaded()],
		async ([userId, loaded]) => {
			if (!userId || !loaded) return "";
			return generatePfp(userId as string);
		},
	);

	return (
		<svg
			aria-hidden="true"
			class="avatar"
			data-status={props.user?.presence?.status ?? "Offline"}
			data-user-id={props.user?.id}
			viewBox={`0 0 ${totalSize()} ${totalSize()}`}
			role="img"
			style={`--pad: ${pad()}px; ${props.style ?? ""}`}
			classList={{ autogenerated: !props.user?.avatar }}
		>
			<defs>
				<mask id="rbox2">
					<rect
						rx="6"
						width={size}
						height={size}
						x={pad()}
						y={pad()}
						fill="white"
					/>
				</mask>
			</defs>
			<g mask="url(#rbox2)">
				<Show
					when={props.user?.avatar}
					fallback={
						<image
							preserveAspectRatio="xMidYMid slice"
							width={size}
							height={size}
							x={pad()}
							y={pad()}
							href={pfp() ?? ""}
						/>
					}
				>
					{(avatar) => (
						<>
							<rect
								width={size}
								height={size}
								x={pad()}
								y={pad()}
								fill="oklch(var(--color-bg3))"
							/>
							<image
								// temp? i need to crop avatars properly on upload
								preserveAspectRatio="xMidYMid slice"
								width={size}
								height={size}
								x={pad()}
								y={pad()}
								href={getThumbFromId(avatar(), 64, props.animate)}
							/>
						</>
					)}
				</Show>
			</g>
		</svg>
	);
};
